import { useEffect } from 'react';

type RelatedDevices = {
  cctv: any[];
  gasDetector: any[];
  smartHook: any[];
};

type DeviceConnectionLinesProps = {
  map: google.maps.Map | null;
  isLoaded: boolean;
  clickedPoint: { lat: number; lng: number } | null;
  relatedDevices: RelatedDevices;
};

const LINE_STYLE = { color: '#FFFFFF', opacity: 0.8, weight: 3 };
const CURVE_SEGMENTS = 30;

function buildCurvePath(
  start: { lat: number; lng: number },
  end: { lat: number; lng: number }
): google.maps.LatLngLiteral[] {
  const midLat = (start.lat + end.lat) / 2;
  const midLng = (start.lng + end.lng) / 2;

  const dLat = end.lat - start.lat;
  const dLng = end.lng - start.lng;
  const dist = Math.sqrt(dLat * dLat + dLng * dLng);

  const angle = ((Math.atan2(dLat, dLng) * 180) / Math.PI + 360) % 360;
  const flip = angle > 90 && angle < 270 ? -1 : 1;

  const control = {
    lat: midLat + dLng * 0.3 * flip,
    lng: midLng - dLat * 0.3 * flip
  };

  const offset = dist * 0.15;
  control.lat += offset * (dLng >= 0 ? 1 : -1) * flip;

  const points: google.maps.LatLngLiteral[] = [];
  for (let i = 0; i <= CURVE_SEGMENTS; i++) {
    const t = i / CURVE_SEGMENTS;
    const u = 1 - t;
    points.push({
      lat: u * u * start.lat + 2 * u * t * control.lat + t * t * end.lat,
      lng: u * u * start.lng + 2 * u * t * control.lng + t * t * end.lng
    });
  }
  return points;
}

export function DeviceConnectionLines({
  map,
  isLoaded,
  clickedPoint,
  relatedDevices
}: DeviceConnectionLinesProps) {
  useEffect(() => {
    if (!isLoaded || !map || !clickedPoint) return;

    const center = clickedPoint;

    const polylines: google.maps.Polyline[] = [];

    const drawLines = (devices: { lat: number; lng: number }[]) => {
      devices.forEach((device) => {
        if (!Number.isFinite(device.lat) || !Number.isFinite(device.lng)) return;

        const polyline = new google.maps.Polyline({
          map,
          path: buildCurvePath(center, { lat: device.lat, lng: device.lng }),
          strokeColor: LINE_STYLE.color,
          strokeOpacity: LINE_STYLE.opacity,
          strokeWeight: LINE_STYLE.weight,
          geodesic: false
        });
        polylines.push(polyline);
      });
    };

    drawLines(relatedDevices.cctv);
    drawLines(relatedDevices.gasDetector);
    drawLines(relatedDevices.smartHook);

    return () => {
      polylines.forEach((p) => p.setMap(null));
    };
  }, [map, isLoaded, clickedPoint, relatedDevices]);

  return null;
}
